import { Check, Eye, Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { AttachmentPrimitive, attachmentFileId, takeBackThreadFile, useAuiState } from "../aui";
import { FileName } from "./FileName";
import { useThreadFileState } from "./fileState";
import { threadFileUrl } from "./files";
import { canPreview, FilePreviewDialog, type PreviewFile } from "./FilePreviewDialog";
import { useNotice } from "./notice";
import { PagesPicker } from "./PagesPicker";
import { useDropMentions } from "./useComposerMentions";
import { useThreadId } from "./useThreadId";

/** The chip buttons' line box (24px; 36px on a phone), beside the name's first line. */
const LINE = "flex h-6 shrink-0 items-center max-sm:h-9";

/**
 * A file on a message or in the composer: its name, an eye to preview it, a spinner while it
 * uploads, and (in the composer) a cross to take it off, with its mentions in the text, and, on a
 * PDF, the pages to send. A failed upload turns red with its reason. A long name wraps, never cut
 * (it may be all that tells two files apart); the icons and buttons stay by its first line.
 */
export function AttachmentChip({ removable = false }: { removable?: boolean }) {
  const attachment = useAuiState((state) => state.attachment);
  const threadId = useThreadId();
  // An upload's too, once it finished: its pages are picked like any draft PDF's.
  const fileId = attachmentFileId(attachment.id);
  const filed = useThreadFileState(fileId) === "filed";
  const [preview, setPreview] = useState<PreviewFile | null>(null);
  const dropMentions = useDropMentions();
  const { setNotice } = useNotice();
  const status = attachment.status;
  const uploading = status.type === "running";
  const failed = status.type === "incomplete";
  const local = attachment.file;
  const contentType = attachment.contentType || local?.type || "";
  // Once uploaded: an uploading or refused file has nothing to show yet.
  const previewable =
    status.type !== "running" &&
    !failed &&
    canPreview(contentType) &&
    (!!local || (fileId != null && threadId != null));
  const extension = attachment.name.includes(".") ? attachment.name.split(".").pop() : null;

  useEffect(() => {
    if (!preview?.url.startsWith("blob:")) return;
    const url = preview.url;
    return () => URL.revokeObjectURL(url);
  }, [preview]);

  const open = async () => {
    // A file still in the composer previews from this device; a sent one from a short-lived link.
    const url = local
      ? URL.createObjectURL(local)
      : fileId != null && threadId != null
        ? await threadFileUrl(threadId, fileId)
        : null;
    // No link (the request failed): said as the @ list's eye says it, not a press that does nothing.
    if (url) setPreview({ name: attachment.name, url, contentType });
    else setNotice("That file can't be previewed.");
  };

  return (
    <AttachmentPrimitive.Root
      title={failed ? `${attachment.name}: ${status.message ?? ""}` : undefined}
      // Filed is said in words too, not by colour alone.
      {...(filed && { role: "group", "aria-label": `${attachment.name}, filed` })}
      className={cn(
        "flex min-h-8 min-w-0 max-w-full items-start gap-1 rounded-md border bg-card py-[3px] pl-2.5 pr-1 text-xs",
        // On touch the eye and the cross sit apart, so their 44px targets don't overlap.
        "[@media(pointer:coarse)]:[&_[data-compact]]:mx-2.5",
        // Phones: flat, no card inside the composer; the taller buttons fill the chip.
        "max-sm:border-0 max-sm:bg-muted max-sm:py-0",
        failed && "border-destructive/40 text-destructive max-sm:bg-destructive/10",
        filed && "border-success/30 bg-success-soft text-success"
      )}
    >
      {filed && (
        <span className={LINE}>
          <Check className="h-3.5 w-3.5" aria-hidden />
        </span>
      )}
      {/* Its first line centred on the buttons' line box. */}
      <span className="min-w-0 break-words py-1 font-medium leading-4 max-sm:py-2.5">
        {failed ? (
          `Can't attach ${extension ? `.${extension}` : "this file"}`
        ) : (
          <FileName name={attachment.name} />
        )}
        {/* The reason is in the tooltip for a pointer; said here for everyone else. */}
        {failed && status.message && <span className="sr-only">: {status.message}</span>}
      </span>
      {previewable && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0 text-muted-foreground max-sm:h-9 max-sm:w-9"
          aria-label={`Preview ${attachment.name}`}
          data-compact=""
          onClick={() => void open()}
        >
          <Eye className="!size-3.5" />
        </Button>
      )}
      {removable && contentType === "application/pdf" && fileId != null && threadId != null && (
        <PagesPicker threadId={threadId} fileId={fileId} name={attachment.name} />
      )}
      {uploading && (
        <span className={LINE}>
          <Loader2
            className="mx-1 h-3.5 w-3.5 text-muted-foreground motion-safe:animate-spin"
            aria-label="Uploading"
          />
        </span>
      )}
      {removable && (
        <AttachmentPrimitive.Remove asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0 text-muted-foreground max-sm:h-9 max-sm:w-9"
            aria-label={`Remove ${attachment.name}`}
            data-compact=""
            // A file already on the thread (a document added, a failed send taken back) is taken
            // off the draft here; an upload is, by the attachment adapter. A sent file's link
            // stays (409).
            onClick={() => {
              dropMentions(attachment.name, attachmentFileId(attachment.id));
              if (status.type === "complete" && fileId != null && threadId != null) {
                void takeBackThreadFile(threadId, fileId);
              }
            }}
          >
            <X className="!size-3.5" />
          </Button>
        </AttachmentPrimitive.Remove>
      )}
      <FilePreviewDialog file={preview} onClose={() => setPreview(null)} />
    </AttachmentPrimitive.Root>
  );
}
